import { useRef, useState, useEffect } from "react";
import debounce from "lodash-es/debounce";
import isEqual from "lodash-es/isEqual";
import * as Sentry from "@sentry/browser";

import { updateCheckout } from "./actions";
import { getUndef, getDataFromItemsToChange, getIngredients } from "./utils";
import { useItemsToChange } from "./hooks";

export default function useLineItems({
  checkoutId,
  paymentId,
  client,
  setCheckout,
  checkoutRef,
}) {
  const checkout = checkoutRef.current;
  const [inited, setInited] = useState(false);
  const [optimisticLineItems, setOptimisticLineItems] = useState([]);
  const activeRequestsCount = useRef(0);
  const activeRequests = useRef<any>();

  useEffect(() => {
    if (inited || !checkoutId || !checkout) {
      return;
    }
    setOptimisticLineItems(getOptimisticLineItems(checkout, []));
    setInited(true);
  }, [checkoutId, checkout]);

  const {
    itemsToChange,
    addItemToChange,
    removeItemToChange,
    resetItemsToChange,
  } = useItemsToChange();

  const {
    itemsToChange: optimisticItemsToChange,
    addItemToChange: optimisticAddItemToChange,
    removeItemToChange: optimisticRemoveItemToChange,
    resetItemsToChange: optimisticResetItemsToChange,
  } = useItemsToChange();

  function currUpdateCheckout(checkoutId, paymentId) {
    if (!activeRequests.current) {
      activeRequestsCount.current++;
      activeRequests.current = updateCheckout(
        client,
        checkoutId,
        paymentId,
        getDataFromItemsToChange(itemsToChange.current, checkoutRef.current)
      );
      resetItemsToChange();
    } else {
      activeRequestsCount.current++;
      activeRequests.current = activeRequests.current.then(async (checkout) => {
        const checkoutPromise = updateCheckout(
          client,
          checkoutId,
          paymentId,
          getDataFromItemsToChange(itemsToChange.current, checkout)
        );
        resetItemsToChange();
        return checkoutPromise;
      });
    }
    activeRequests.current = activeRequests.current
      .then((checkout) => {
        activeRequestsCount.current--;
        if (activeRequestsCount.current <= 0) {
          setCheckout(checkout);
          activeRequests.current = undefined;
          setOptimisticLineItems(
            getOptimisticLineItems(checkoutRef.current, [])
          );
          optimisticResetItemsToChange();
          return false;
        } else {
          return checkout;
        }
      })
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    return activeRequests.current;
  }

  const debUpdateCheckout = useRef(
    debounce(currUpdateCheckout, 200, {
      leading: true,
    })
  ).current;

  const ingredientIds =
    getUndef(() =>
      checkout.lineItems.reduce((acc, { customAttributes }) => {
        const itemIngredients = getIngredients(
          customAttributes.filter((attr) => attr.key === "ingredient")
        );
        itemIngredients.forEach(({ id }) => {
          const added = acc.find((item) => item === id);
          if (!added) {
            acc.push(id);
          }
        });
        return acc;
      }, [])
    ) || [];

  return {
    addToCart: async (props) => {
      addItemToChange(props);
      optimisticAddItemToChange(props);

      setOptimisticLineItems(
        getOptimisticLineItems(
          checkoutRef.current,
          optimisticItemsToChange.current
        )
      );

      await debUpdateCheckout(checkoutId, paymentId);
    },
    removeFromCart: async (props) => {
      removeItemToChange(props);
      optimisticRemoveItemToChange(props);

      setOptimisticLineItems(
        getOptimisticLineItems(
          checkoutRef.current,
          optimisticItemsToChange.current
        )
      );

      await debUpdateCheckout(checkoutId, paymentId);
    },
    ingredientIds,
    optimisticLineItems,
    updateOptimisticLineItems: () => {
      setOptimisticLineItems(getOptimisticLineItems(checkoutRef.current, []));
    },
  };
}

function getOptimisticLineItems(checkout: any, itemsToChangeState: any) {
  return checkout.lineItems.map((item) => {
    const {
      quantity,
      price,
      customAttributes,
      variant: { id, price: variantPrice },
    } = item;

    const ingredients = getIngredients(
      customAttributes.filter((attr) => attr.key === "ingredient")
    );

    const ingredientsPrice = ingredients
      .map((ingredient) => ({
        price:
          getUndef(() => {
            return checkout.lineItems.find(
              (lineItem) => lineItem.variant.id === ingredient.id
            ).variant.price;
          }) || 0,
        quantity: ingredient.amount,
      }))
      .reduce((acc, val) => acc + val.price * val.quantity, 0);

    const optimisticItem = itemsToChangeState.find(
      ({ id: itemId, ingredients: itemIngredients }) =>
        itemId === id && isEqual(ingredients, itemIngredients)
    );
    const expectedQuantity =
      (getUndef(() => optimisticItem.quantity) || 0) + quantity;
    let expectedPrice =
      expectedQuantity === quantity ? price : variantPrice * expectedQuantity;
    expectedPrice = expectedPrice + ingredientsPrice * expectedQuantity;

    return {
      ...item,
      expectedQuantity,
      expectedPrice,
      price,
    };
  });
}
